import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useRooms } from '../context/RoomContext';
import { useCharacter } from '../context/CharacterContext';
import { MetaTags } from './MetaTags';
import { roomThumbUrl } from '../utils/inviteLink';
import { UNITY_BUILD_BASE, UNITY_PRELOAD_FILES } from '../config/unityBuild';
import './RoomJoin.css';

/**
 * /room/:roomCode – shareable room link page.
 *
 * Flow:
 *  1. Extract roomCode from URL
 *  2. Show OG-friendly meta tags (room name + invite)
 *  3. If user is logged in and rooms are loaded → auto-join the room
 *  4. Once joined, signal parent that the room is ready (via onJoined callback)
 *  5. If not logged in → show invite card + "Sign in to join" CTA
 */

export interface RoomJoinProps {
  /** Called when join succeeds, so the parent can render the game view. */
  onJoined?: () => void;
  /** Whether the game is already being shown (user already in room). */
  isInRoom?: boolean;
  children?: React.ReactNode;
}

export function RoomJoin({ onJoined, isInRoom, children }: RoomJoinProps) {
  const { roomCode } = useParams<{ roomCode: string }>();
  const [searchParams] = useSearchParams();
  // Invite context carried on the link (?from=who&note=text) by ShareRoomModal
  // / the OG shim redirect. Surfaced on the join card so the recipient sees who
  // invited them and the personal note.
  const inviterName = searchParams.get('from');
  const inviteNote = searchParams.get('note');
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useUser();
  const { rooms, currentRoom, joinRoom, setCurrentRoom, isConnected, refreshRooms, findRoomByCode } = useRooms();
  const { activeCharacter } = useCharacter();

  const [status, setStatus] = useState<'loading' | 'joining' | 'joined' | 'error' | 'needs-login' | 'needs-password'>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [hostName, setHostName] = useState<string | null>(null);
  const [playerCount, setPlayerCount] = useState<string>('');
  const joinAttempted = useRef(false);

  // If the user is already in this room (navigated from dashboard), skip join flow
  const alreadyInRoom = isInRoom || (currentRoom && currentRoom.code === roomCode);

  // Find the room by code once rooms are loaded
  const targetRoom = rooms.find(r => r.code === roomCode);

  // Update display info when room is found
  useEffect(() => {
    if (targetRoom) {
      setRoomName(targetRoom.name);
      setHostName(targetRoom.hostName);
      setPlayerCount(`${targetRoom.currentPlayers}/${targetRoom.maxPlayers}`);
    }
  }, [targetRoom]);

  // Update document title when in room
  useEffect(() => {
    const name = currentRoom?.name || roomName;
    if (name) {
      document.title = `${name} — GameDate`;
    }
    return () => { document.title = 'GameDate — Find your perfect match through adventure'; };
  }, [currentRoom?.name, roomName]);

  // Resolve the target room: fast single-room lookup by code first (saves the
  // full-list roundtrip on every deep link); any miss or error falls back to
  // the classic full refresh so not-found handling and older backends keep
  // working unchanged.
  useEffect(() => {
    if (isConnected && rooms.length === 0 && roomCode) {
      findRoomByCode(roomCode).then((result) => {
        if (result !== 'found') refreshRooms();
      });
    }
  }, [isConnected, rooms.length, roomCode, findRoomByCode, refreshRooms]);

  // Warm-start the Unity build download in parallel with auth + join — the
  // measured join chain (connect → ReconnectUser → rooms → join) serializes
  // ~3-5 s BEFORE Unity's own fetches begin. Gated on the service worker
  // controlling the page: the SW coalesces these with Unity's later requests
  // for the same URLs, so this can never double-download (which unguarded
  // parallel fetches measurably did).
  useEffect(() => {
    if (!roomCode || !navigator.serviceWorker?.controller) return;
    // The data file lives in Unity's OWN IndexedDB cache after a successful
    // load (UnityGame stamps localStorage with its hash) — warm-fetching it
    // again measurably re-downloaded 24 MB per visit, because the SW
    // deliberately doesn't Cache-API-pin a file Unity already stores.
    const cachedDataHash = localStorage.getItem('gamedate_unity_data_v');
    for (const file of UNITY_PRELOAD_FILES) {
      const isDataFile = file.path.includes('.data');
      if (isDataFile && cachedDataHash && file.path.includes(cachedDataHash)) continue;
      fetch(`${UNITY_BUILD_BASE}${file.path}`, { priority: 'low' } as RequestInit).catch(() => {});
    }
  }, [roomCode]);

  // If not logged in, prompt to sign in
  useEffect(() => {
    if (!userLoading && !user) {
      setStatus('needs-login');
    }
  }, [userLoading, user]);

  // Auto-join when we have all the pieces
  const attemptJoin = useCallback(async () => {
    if (!targetRoom || !user || joinAttempted.current) return;
    joinAttempted.current = true;
    setStatus('joining');

    try {
      const charGender = activeCharacter?.gender || user.gender;
      const charClass = activeCharacter?.characterClass || user.characterClass;
      const legacyGender = (charGender === 'woman' || charGender === 'female') ? 'female' : 'male';
      const genderSuffix = legacyGender === 'female' ? 'Girl' : 'Boy';
      const characterType = `${charClass}_${genderSuffix}`;

      const success = await joinRoom(
        targetRoom.id,
        user.id,
        user.displayName,
        user.avatar,
        characterType,
        legacyGender,
        undefined, // password
        activeCharacter?.appearanceData,
      );

      if (success) {
        setCurrentRoom(targetRoom);
        setStatus('joined');
        onJoined?.();
      } else {
        setStatus('error');
        setErrorMsg('Could not join the room. It may be full.');
      }
    } catch (err) {
      const error = err as Error;
      if (error.message === 'PASSWORD_REQUIRED') {
        setStatus('needs-password');
      } else if (error.message === 'APPROVAL_REQUIRED') {
        setStatus('error');
        setErrorMsg('This room requires host approval to join.');
      } else {
        setStatus('error');
        setErrorMsg('Failed to join room. Please try again.');
      }
    }
  }, [targetRoom, user, activeCharacter, joinRoom, setCurrentRoom, onJoined]);

  useEffect(() => {
    if (alreadyInRoom) {
      setStatus('joined');
      return;
    }
    if (user && targetRoom && !joinAttempted.current) {
      attemptJoin();
    } else if (user && isConnected && rooms.length > 0 && !targetRoom) {
      // Rooms loaded but code not found
      setStatus('error');
      setErrorMsg(`Room "${roomCode}" not found. It may have been closed or the link is invalid.`);
    }
  }, [alreadyInRoom, user, targetRoom, isConnected, rooms.length, roomCode, attemptJoin]);

  const ogTitle = roomName
    ? `Join "${roomName}" on GameDate`
    : 'You\'re invited to GameDate';
  const ogDescription = roomName && hostName
    ? `${hostName} invited you to join "${roomName}". ${playerCount ? `${playerCount} players` : ''} Jump in and play!`
    : 'Join a multiplayer room, build your character, and meet people through shared adventures.';

  // If already in room / just joined, render the game view (passed as children)
  if (alreadyInRoom || status === 'joined') {
    return (
      <>
        <MetaTags
          title={currentRoom ? `${currentRoom.name} — GameDate` : ogTitle}
          description={currentRoom
            ? `${currentRoom.hostName} is hosting "${currentRoom.name}". ${currentRoom.currentPlayers}/${currentRoom.maxPlayers} players — join the adventure!`
            : ogDescription
          }
          image={(currentRoom ?? targetRoom)?.sceneTemplateId ? roomThumbUrl((currentRoom ?? targetRoom)!) : undefined}
          url={`https://gamedate.co.il/room/${roomCode}`}
        />
        {children}
      </>
    );
  }

  return (
    <>
      <MetaTags
        title={ogTitle}
        description={ogDescription}
        image={targetRoom?.sceneTemplateId ? roomThumbUrl(targetRoom) : undefined}
        url={`https://gamedate.co.il/room/${roomCode}`}
      />

      <div className="room-join-page">
        <div className="room-join-card">
          <div className="room-join-logo">🎮 GameDate</div>

          {status === 'loading' && (
            <>
              <h1>Finding room…</h1>
              <div className="room-join-spinner" />
              <p className="room-join-subtitle">Looking up invite code <strong>{roomCode}</strong></p>
            </>
          )}

          {status === 'joining' && (
            <>
              <h1>Joining {roomName || 'room'}…</h1>
              <div className="room-join-spinner" />
              {hostName && <p className="room-join-subtitle">Hosted by {hostName}</p>}
              {playerCount && <p className="room-join-meta">{playerCount} players</p>}
            </>
          )}

          {status === 'needs-login' && (
            <>
              {inviterName && (
                <p className="room-join-inviter">🎉 <strong>{inviterName}</strong> invited you</p>
              )}
              <h1>{roomName ? `Join "${roomName}"` : "You're invited!"}</h1>
              {inviteNote && <p className="room-join-note">“{inviteNote}”</p>}
              {hostName && <p className="room-join-subtitle">Hosted by {hostName}</p>}
              {playerCount && <p className="room-join-meta">{playerCount} players</p>}
              <p className="room-join-cta-text">Sign in or create an account to join this room</p>
              <button className="room-join-btn primary" onClick={() => navigate('/', { state: { pendingRoomCode: roomCode } })}>
                Sign in to Join
              </button>
            </>
          )}

          {status === 'needs-password' && (
            <>
              <h1>{roomName ? `Join "${roomName}"` : 'Room requires password'}</h1>
              <p className="room-join-subtitle">This room is password-protected</p>
              <button className="room-join-btn primary" onClick={() => navigate('/', { state: { pendingRoomCode: roomCode } })}>
                Go to Rooms
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <h1>Oops!</h1>
              <p className="room-join-error">{errorMsg}</p>
              <button className="room-join-btn primary" onClick={() => navigate('/')}>
                Browse Rooms
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
